import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Flame, ChevronLeft, ChevronRight } from "lucide-react";

const PODIUM = [
  { h: "h-24", tone: "bg-gradient-to-t from-amber-400 to-yellow-300", ring: "ring-amber-400", order: "order-2" },
  { h: "h-16", tone: "bg-gradient-to-t from-slate-300 to-slate-200", ring: "ring-slate-300", order: "order-1" },
  { h: "h-12", tone: "bg-gradient-to-t from-orange-400 to-amber-300", ring: "ring-orange-300", order: "order-3" },
];
const shiftMonth = (ym, dir) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + dir, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const label = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

export default function StreakPodium() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/streaks", { params: { month } }).then(({ data }) => setData(data)).catch(() => setData(null)); }, [month]);
  if (!data) return null;
  const top = data.board.slice(0, 3);
  const rest = data.board.slice(3);
  const initials = (n) => (n || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Card className="p-6 border-slate-200 shadow-none" data-testid="streak-podium">
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Consistency</div>
          <h3 className="font-heading font-bold text-xl flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" /> Longest streak podium</h3>
          <p className="text-xs text-slate-500 mt-1 font-mono-plex">Consecutive weeks hitting every target · {data.weeks_in_month} weeks in month</p>
        </div>
        <div className="flex items-center border border-slate-200 rounded-md bg-white">
          <button className="p-1.5 hover:bg-slate-50" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="streak-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-2 text-xs font-mono-plex min-w-[120px] text-center" data-testid="streak-month">{label(data.month)}</span>
          <button className="p-1.5 hover:bg-slate-50" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="streak-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {top.length === 0 ? <div className="text-sm text-slate-400">No reps yet.</div> : (
        <div className="flex items-end justify-center gap-3 pt-6">
          {top.map((r, i) => (
            <div key={r.user_id} className={`flex flex-col items-center w-28 ${PODIUM[i].order}`} data-testid={`podium-${i + 1}`}>
              <Avatar className={`w-10 h-10 ring-2 ${PODIUM[i].ring} mb-1`}>{r.picture ? <AvatarImage src={r.picture} /> : null}<AvatarFallback className="bg-slate-200 text-xs">{initials(r.name)}</AvatarFallback></Avatar>
              <div className="text-xs font-medium text-slate-900 truncate max-w-full">{r.name}</div>
              <div className="text-[10px] text-slate-500 font-mono-plex mb-1">{r.month_weeks_hit}/{r.month_weeks} wks hit</div>
              <div className={`w-full ${PODIUM[i].h} ${PODIUM[i].tone} rounded-t-lg flex flex-col items-center justify-center text-slate-900`}>
                <div className="font-heading font-black text-2xl leading-none" data-testid={`podium-${i + 1}-streak`}>{r.month_longest}</div>
                <div className="text-[9px] uppercase tracking-widest font-mono-plex">wk streak</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
          {rest.map((r, i) => (
            <div key={r.user_id} className="py-2 flex items-center justify-between text-sm" data-testid={`streak-row-${r.user_id}`}>
              <div className="flex items-center gap-2"><span className="text-xs font-mono-plex text-slate-400">#{i + 4}</span><span className="text-slate-900">{r.name}</span></div>
              <div className="flex items-center gap-3 text-xs font-mono-plex text-slate-500"><span>{r.month_longest} wk streak</span><span>{r.month_weeks_hit}/{r.month_weeks} hit</span><span className="flex gap-0.5">{r.recent.map((h, j) => <span key={j} className={`w-2 h-2 rounded-sm ${h ? "bg-emerald-500" : "bg-slate-200"}`} />)}</span></div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono-plex text-slate-500">
        {data.board.slice(0, 3).map(r => <span key={r.user_id}>{r.name}: current {r.current_streak} · best {r.longest_streak} (52 wks)</span>)}
      </div>
    </Card>
  );
}
